import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";

type FarmingMatch = {
  source: "tombstone" | "signup_request";
  label: string;
  matchedOn: ("address" | "phone")[];
  date: string;
};

type SignupRequest = {
  id: number;
  firstName: string;
  lastName: string;
  restaurantName: string;
  restaurantAddress: string;
  phone: string;
  email: string;
  businessId: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
  reviewedBy: { id: number; name: string | null; email: string } | null;
  farmingMatches: FarmingMatch[];
};

const SignupRequests: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<SignupRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const query = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const res = await apiFetch(`/api/superadmin/signup-requests${query}`);
      if (res?.ok) {
        setRequests(await res.json());
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApprove = async (id: number) => {
    setActioningId(id);
    setMessage(null);
    setError(null);
    try {
      const res = await apiFetch(`/api/superadmin/signup-requests/${id}/approve`, { method: "POST" });
      const data = await res?.json();
      if (!res?.ok) {
        setError(data?.error || t("signupRequests.approveFailed"));
      } else {
        setMessage(t("signupRequests.approveSuccess", { restaurant: data.restaurant?.name }));
        load();
      }
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (id: number) => {
    setActioningId(id);
    setMessage(null);
    setError(null);
    try {
      const res = await apiFetch(`/api/superadmin/signup-requests/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: rejectReason || undefined }),
      });
      const data = await res?.json();
      if (!res?.ok) {
        setError(data?.error || t("signupRequests.rejectFailed"));
      } else {
        setMessage(t("signupRequests.rejectSuccess"));
        setRejectingId(null);
        setRejectReason("");
        load();
      }
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
        <h1 className="text-2xl font-bold">{t("signupRequests.title")}</h1>
        <select
          className="border rounded p-2 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="pending">{t("signupRequests.filterPending")}</option>
          <option value="approved">{t("signupRequests.filterApproved")}</option>
          <option value="rejected">{t("signupRequests.filterRejected")}</option>
          <option value="all">{t("signupRequests.filterAll")}</option>
        </select>
      </div>

      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3">{message}</div>}
      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      {loading ? (
        <div className="text-gray-500">{t("common.loading")}</div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          {t("signupRequests.noneFound")}
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-xl shadow p-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <div className="font-semibold text-gray-900">
                    {r.restaurantName}
                    <span
                      className={`ml-2 px-2 py-0.5 rounded text-xs uppercase ${
                        r.status === "pending"
                          ? "bg-amber-100 text-amber-700"
                          : r.status === "approved"
                          ? "bg-teal-100 text-teal-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {/* Same trap as the system-health badge: the API's own
                          word was rendering untranslated on the French UI. */}
                      {t(`signupRequests.status.${r.status}`, { defaultValue: r.status })}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {r.firstName} {r.lastName} &middot; {r.email} &middot; {r.phone}
                  </div>
                  <div className="text-sm text-gray-600">{r.restaurantAddress}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {t("signupRequests.businessId")}: {r.businessId}
                  </div>
                  <div className="text-xs text-gray-400">
                    {t("signupRequests.submitted", { date: new Date(r.createdAt).toLocaleDateString() })}
                  </div>
                  {r.status === "rejected" && r.rejectionReason && (
                    <div className="text-xs text-red-500 mt-1">{t("signupRequests.reason")}: {r.rejectionReason}</div>
                  )}
                  {r.farmingMatches.length > 0 && (
                    <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
                      <div className="font-semibold">
                        {t("signupRequests.farmingWarning", { count: r.farmingMatches.length })}
                      </div>
                      <ul className="mt-1 list-disc list-inside space-y-0.5">
                        {r.farmingMatches.map((m, i) => (
                          <li key={i}>
                            {m.label} &middot;{" "}
                            {t(
                              m.source === "tombstone"
                                ? "signupRequests.farmingSourceTombstone"
                                : "signupRequests.farmingSourceRequest"
                            )}{" "}
                            &middot;{" "}
                            {m.matchedOn
                              .map((f) =>
                                t(
                                  f === "address"
                                    ? "signupRequests.farmingMatchedAddress"
                                    : "signupRequests.farmingMatchedPhone"
                                )
                              )
                              .join(", ")}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex flex-col gap-2 md:items-end shrink-0">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(r.id)}
                        disabled={actioningId === r.id}
                        className="bg-teal-600 text-white text-sm px-3 py-1.5 rounded font-semibold hover:bg-teal-700 transition disabled:opacity-50"
                      >
                        {t("signupRequests.approve")}
                      </button>
                      <button
                        onClick={() => setRejectingId(rejectingId === r.id ? null : r.id)}
                        disabled={actioningId === r.id}
                        className="bg-white border border-red-300 text-red-600 text-sm px-3 py-1.5 rounded font-semibold hover:bg-red-50 transition disabled:opacity-50"
                      >
                        {t("signupRequests.reject")}
                      </button>
                    </div>
                    {rejectingId === r.id && (
                      <div className="flex flex-col gap-2 w-full md:w-64">
                        <input
                          className="border rounded p-2 text-sm"
                          type="text"
                          placeholder={t("signupRequests.reasonPlaceholder")}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                        />
                        <button
                          onClick={() => handleReject(r.id)}
                          disabled={actioningId === r.id}
                          className="bg-red-600 text-white text-sm px-3 py-1.5 rounded font-semibold hover:bg-red-700 transition disabled:opacity-50"
                        >
                          {t("signupRequests.confirmReject")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SignupRequests;
